import {
  Button,
  HStack,
  IconButton,
  Link,
  Menu,
  Portal,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronsDown,
  FiChevronsUp,
  FiMoreVertical,
  FiRefreshCw,
  FiRotateCcw,
} from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { PageHeaderPortal } from '#components/page-header-portal';
import { useAppConfig } from '#providers/app-config-provider';
import { usePageContentSidebar } from '#providers/page-layout-provider';
import { documentFilesHref } from '#utils/document-app-path';

import { useDocumentContext } from '../document-context';
import { parseEditorJs } from '../editorjs-types';

import { AdvancedDocumentModal } from './modals/advanced-document-modal';
import { CreateSubpageModal } from './modals/create-subpage-modal';
import { editorDataHasLeadingFilesSection } from './viewer/editor-files-section';

interface ViewDocumentHeaderProps {
  onNavigateToHistory: () => void;
  isCreateSubpageOpen: boolean;
  onCreateSubpageOpenChange: (open: boolean) => void;
  /** Matches document editor mobile breakpoint (e.g. 650px). */
  isMobileEditorLayout: boolean;
  composerFooterVisible: boolean;
  onComposerFooterToggle: () => void;
}

export function ViewDocumentHeader({
  onNavigateToHistory,
  isCreateSubpageOpen,
  onCreateSubpageOpenChange,
  isMobileEditorLayout,
  composerFooterVisible,
  onComposerFooterToggle,
}: ViewDocumentHeaderProps) {
  const navigate = useNavigate();
  const {
    document,
    displayPath,
    documentId,
    isNewDocument,
    isDirty,
    dirtyEditGeneration,
    getSerializedDocument,
    save,
    saving,
    saveInFlightRef,
    revertToSaved,
  } = useDocumentContext();
  const { config, setDocumentAutoSaveEnabled } = useAppConfig();
  const {
    contentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
  } = usePageContentSidebar();
  const { documentAutoSaveEnabled: autoSaveEnabled } = config;
  const isDirtyRef = useRef(isDirty);
  const autoSaveEnabledRef = useRef(autoSaveEnabled);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const saveRef = useRef<(options?: { silent?: boolean }) => Promise<void>>(
    () => Promise.resolve(),
  );

  const showDocumentFilesLink = useMemo(() => {
    void dirtyEditGeneration;
    if (document?.__typename !== 'Document' || isNewDocument) return false;

    try {
      return editorDataHasLeadingFilesSection(
        parseEditorJs(getSerializedDocument()) ?? null,
      );
    } catch {
      return false;
    }
  }, [document, dirtyEditGeneration, getSerializedDocument, isNewDocument]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    autoSaveEnabledRef.current = autoSaveEnabled;
  }, [autoSaveEnabled]);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  useEffect(() => {
    if (!autoSaveEnabled || !isDirty) return;

    const id = window.setTimeout(() => {
      if (
        !autoSaveEnabledRef.current ||
        !isDirtyRef.current ||
        saveInFlightRef.current
      ) {
        return;
      }

      void saveRef.current({ silent: true });
    }, 5000);
    return () => window.clearTimeout(id);
  }, [autoSaveEnabled, dirtyEditGeneration, isDirty, saveInFlightRef]);

  const autoSaveToggleButton = useMemo(
    () => (
      <IconButton
        aria-label={
          autoSaveEnabled
            ? 'Disable auto-save'
            : 'Enable auto-save (5 seconds after you stop editing)'
        }
        variant={autoSaveEnabled ? 'solid' : 'ghost'}
        colorPalette={autoSaveEnabled ? 'blue' : 'gray'}
        size="sm"
        title={
          autoSaveEnabled
            ? 'Disable auto-save'
            : 'Enable auto-save (5 seconds after you stop editing)'
        }
        loading={saving}
        disabled={saving}
        onClick={() => setDocumentAutoSaveEnabled(!autoSaveEnabled)}
      >
        <FiRefreshCw />
      </IconButton>
    ),
    [autoSaveEnabled, saving, setDocumentAutoSaveEnabled],
  );

  const revertButton = useMemo(
    () => (
      <IconButton
        aria-label="Revert to last saved"
        variant="ghost"
        size="sm"
        title="Revert to last saved version"
        onClick={revertToSaved}
      >
        <FiRotateCcw />
      </IconButton>
    ),
    [revertToSaved],
  );

  const actionsContent = !isNewDocument ? (
    isDirty ? (
      <HStack gap={2}>
        {revertButton}
        {autoSaveEnabled ? (
          autoSaveToggleButton
        ) : (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => void save()}
            loading={saving}
            loadingText="Saving..."
          >
            Save
          </Button>
        )}
      </HStack>
    ) : (
      autoSaveToggleButton
    )
  ) : null;

  const titleContent = useMemo(() => {
    const rightControls = (
      <HStack gap={2} align="center">
        {showDocumentFilesLink && documentId && (
          <Link
            asChild
            fontSize="sm"
            color="blue.600"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={documentFilesHref(displayPath)}>Files</RouterLink>
          </Link>
        )}
        {(isMobileEditorLayout ||
          (contentSidebarSlotActive && contentSidebarDismissed)) && (
          <IconButton
            aria-label={
              isMobileEditorLayout
                ? composerFooterVisible
                  ? 'Hide AI sidebar'
                  : 'Show AI sidebar'
                : 'Show AI sidebar'
            }
            variant="ghost"
            size="sm"
            title={
              isMobileEditorLayout
                ? composerFooterVisible
                  ? 'Hide AI sidebar'
                  : 'Show AI sidebar'
                : 'Show AI sidebar'
            }
            onClick={() => {
              if (isMobileEditorLayout) {
                onComposerFooterToggle();
              } else {
                setContentSidebarDismissed(false);
              }
            }}
          >
            {isMobileEditorLayout ? (
              composerFooterVisible ? (
                <FiChevronsDown />
              ) : (
                <FiChevronsUp />
              )
            ) : (
              <FiChevronsUp />
            )}
          </IconButton>
        )}
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton aria-label="Document actions" variant="ghost" size="sm">
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {documentId && (
                  <Menu.Item
                    value="files"
                    onClick={() => navigate(documentFilesHref(displayPath))}
                  >
                    Files
                  </Menu.Item>
                )}
                {!isNewDocument && (
                  <>
                    <Menu.Item value="history" onClick={onNavigateToHistory}>
                      History
                    </Menu.Item>
                    {documentId && (
                      <Menu.Item
                        value="advanced"
                        onClick={() => setIsAdvancedModalOpen(true)}
                      >
                        Advanced
                      </Menu.Item>
                    )}
                  </>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        {isNewDocument && (
          <HStack gap={2}>
            {isDirty && revertButton}
            {(autoSaveEnabled || !isDirty) && autoSaveToggleButton}
            {isDirty && !autoSaveEnabled && (
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => void save()}
                loading={saving}
                loadingText="Saving..."
              >
                Save
              </Button>
            )}
          </HStack>
        )}
      </HStack>
    );

    return (
      <HStack gap={4} align="center" justify="flex-end" width="100%" minW={0}>
        {rightControls}
      </HStack>
    );
  }, [
    displayPath,
    navigate,
    documentId,
    isNewDocument,
    isMobileEditorLayout,
    composerFooterVisible,
    onComposerFooterToggle,
    contentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
    showDocumentFilesLink,
    onNavigateToHistory,
    save,
    isDirty,
    saving,
    autoSaveEnabled,
    autoSaveToggleButton,
    revertButton,
  ]);

  const pageHeaderTitleSyncKey = useMemo(
    () =>
      [
        displayPath,
        documentId ?? '',
        String(showDocumentFilesLink),
        String(isNewDocument),
        String(isMobileEditorLayout),
        String(composerFooterVisible),
        String(contentSidebarSlotActive),
        String(contentSidebarDismissed),
        String(isDirty),
        String(saving),
        String(autoSaveEnabled),
      ].join('\u001f'),
    [
      displayPath,
      documentId,
      showDocumentFilesLink,
      isNewDocument,
      isMobileEditorLayout,
      composerFooterVisible,
      contentSidebarSlotActive,
      contentSidebarDismissed,
      isDirty,
      saving,
      autoSaveEnabled,
    ],
  );

  return (
    <>
      <PageHeaderPortal
        title={titleContent}
        titleSyncKey={pageHeaderTitleSyncKey}
      >
        {actionsContent}
      </PageHeaderPortal>

      <AdvancedDocumentModal
        open={isAdvancedModalOpen}
        onOpenChange={setIsAdvancedModalOpen}
        documentId={documentId}
        getCurrentValue={getSerializedDocument}
      />

      <CreateSubpageModal
        open={isCreateSubpageOpen}
        onOpenChange={onCreateSubpageOpenChange}
        displayPath={displayPath}
      />
    </>
  );
}
